import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
    Table,
    TableCell,
    TableHead,
    TableRow,
    TableBody,
    TableContainer,
    TableSortLabel,
    Paper,
    Tooltip,
} from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';
import {
    doNotImportScannerCaseTemplate,
    scannerCaseListTemplate,
    triggerScannerCaseSyncTemplate,
} from 'apollo/templates/scanner-case';
import ScannerCaseItem from './scanner-case-item.component';
import { CaseListSourceStatuses, ScannerCase, ScannerCaseFilter, ScannerCaseSortOrder } from 'models/scanner-case';
import { socketIo } from 'context/socket';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { useNavigate } from 'react-router-dom';
import ScannerCaseDialog from 'components/scanner-cases/shared/scanner-case-dialog/scanner-case-dialog.component';

type ScannerCaseTableProps = {
    scannerCaseFilter: ScannerCaseFilter;
    sortOrder: ScannerCaseSortOrder;
    onSortOrderChange: (sortOrder: ScannerCaseSortOrder) => void;
    onScanSourceStatusChange?: (statuses: CaseListSourceStatuses) => void;
};
const ScannerCaseTable: FC<ScannerCaseTableProps> = ({
    scannerCaseFilter,
    sortOrder,
    onSortOrderChange,
    onScanSourceStatusChange,
}: ScannerCaseTableProps) => {
    const { t: translate } = useTranslation(['common', 'scan-cases', 'orders']);
    const [isOpen, setIsOpen] = useState(false);
    const [caseId, setCaseId] = useState<string>('');
    const [lastOffset, setLastOffset] = useState(0);
    const [scannerCases, setScannerCases] = useState<ScannerCase[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();
    const navigate = useNavigate();
    const socket = useContext(socketIo.getSocketContext());
    const organization = currentOrgVar();

    const [getScannerCases, { fetchMore, refetch }] = useLazyQuery(scannerCaseListTemplate, {
        fetchPolicy: 'cache-and-network',
        pollInterval: 5 * 60 * 1000,
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ scannerCases }) => {
            setScannerCases(scannerCases);
            setLastOffset(scannerCases.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [doNotImportScannerCase] = useMutation(doNotImportScannerCaseTemplate, {
        variables: {
            caseId: caseId,
        },
        onCompleted: () => {
            refetch();
            setIsOpen(false);
        },
        onError: (error) => {
            showBoundary(error);
        },
    });

    const [triggerScannerCaseSync] = useMutation(triggerScannerCaseSyncTemplate, {
        onCompleted: ({ triggerScannerCaseSync }) => {
            refetch();
            if (onScanSourceStatusChange) {
                onScanSourceStatusChange(triggerScannerCaseSync);
            }
        },
        onError: (error) => {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setLastOffset(0);
        getScannerCases({
            variables: {
                limit: 20,
                offset: 0,
                filter: scannerCaseFilter,
                sortOrder,
            },
        });
    }, [scannerCaseFilter, sortOrder]);

    useEffect(() => {
        triggerScannerCaseSync();
    }, []);

    useEffect(() => {
        socket.on('SCAN_CASE_SOURCE_STATUS_UPDATED', (event) => {
            if (event.data.organization === organization._id) {
                refetch();
                triggerScannerCaseSync();
            }
        });

        return () => {
            socket.removeAllListeners('SCAN_CASE_SOURCE_STATUS_UPDATED');
        };
    }, [socket]);

    useEffect(() => {
        const handleFilesProcessComplete = () => {
            refetch();
        };

        socket.on('SCANNER_CASE_FILES_PROCESS_COMPLETE', handleFilesProcessComplete);
        return () => {
            socket.off('SCANNER_CASE_FILES_PROCESS_COMPLETE', handleFilesProcessComplete);
        };
    }, [socket]);

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return previousResult;
                    const prevRes = previousResult?.scannerCases || [];
                    const moreRes = fetchMoreResult?.scannerCases || [];
                    return {
                        scannerCases: [...prevRes, ...moreRes],
                    };
                },
            });
        } else {
            getScannerCases({
                variables: {
                    limit: 20,
                    offset: 0,
                    filter: scannerCaseFilter,
                    sortOrder,
                },
            });
        }
    };

    const isOldestFirst = sortOrder === ScannerCaseSortOrder.OLDEST_FIRST;

    const handleSortOrderToggle = () => {
        onSortOrderChange(isOldestFirst ? ScannerCaseSortOrder.NEWEST_FIRST : ScannerCaseSortOrder.OLDEST_FIRST);
    };

    const onScannerCaseClick = (caseId: string) => {
        setCaseId(caseId);
        setIsOpen(true);
    };

    const onDialogClose = () => {
        setIsOpen(false);
    };

    const handleCreateOrderClick = (caseId: string) => {
        navigate(`/orders/create-from-scanner-case/${caseId}`);
    };

    const handleDoNotImportClick = (caseId: string) => {
        doNotImportScannerCase({ variables: { caseId } });
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(handleFetchMore, scannerCases?.length || 0);

    return (
        <Fragment>
            <Fragment>
                <TableContainer component={Paper}>
                    <InfiniteScroll
                        style={{ padding: 0 }}
                        dataLength={scannerCases ? scannerCases.length : 0}
                        next={handleFetchMore}
                        throttleTimeInMs={75}
                        hasMore={true}
                        loader={undefined}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('common:STATUS')}</TableCell>
                                    <TableCell>{translate('scan-cases:SOURCE')}</TableCell>
                                    <TableCell>{translate('common:DENTIST')}</TableCell>
                                    <TableCell>{translate('common:PATIENT')}</TableCell>
                                    <TableCell>{translate('scan-cases:CONSTRUCTION_TYPE')}</TableCell>
                                    <TableCell sortDirection={isOldestFirst ? 'asc' : 'desc'}>
                                        <Tooltip
                                            title={translate(
                                                isOldestFirst
                                                    ? 'scan-cases:SORT_OLDEST_FIRST'
                                                    : 'scan-cases:SORT_NEWEST_FIRST',
                                            )}
                                        >
                                            <TableSortLabel
                                                active
                                                direction={isOldestFirst ? 'asc' : 'desc'}
                                                onClick={handleSortOrderToggle}
                                            >
                                                {translate('common:LAST_UPDATED')}
                                            </TableSortLabel>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{translate('orders:DELIVERY_DATE')}</TableCell>
                                    <TableCell>{translate('scan-cases:COMMENTS')}</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            {scannerCases ? (
                                <TableBody>
                                    {scannerCases.map((scannerCase) => (
                                        <ScannerCaseItem
                                            key={scannerCase._id}
                                            scannerCase={scannerCase}
                                            onScannerCaseClick={onScannerCaseClick}
                                            onDoNotImport={handleDoNotImportClick}
                                            onCreateOrderFromScannerCase={handleCreateOrderClick}
                                        />
                                    ))}
                                </TableBody>
                            ) : undefined}
                        </Table>
                    </InfiniteScroll>
                </TableContainer>
                <ScannerCaseDialog
                    open={isOpen}
                    caseId={caseId}
                    onDialogClose={onDialogClose}
                    onDoNotImport={handleDoNotImportClick}
                    onCreateOrderFromScanCase={handleCreateOrderClick}
                    displayCreateOrderButton
                    displayDoNotImportButton
                />
            </Fragment>
        </Fragment>
    );
};

export default ScannerCaseTable;
